import { TinyLoader } from '@/components/loaders'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import type { getStripePrices } from '@/utils/stripe'
import { trpc } from '@/utils/trpc'
import { useUser } from '@clerk/nextjs'
import { CheckCircle } from 'lucide-react'
import Link from 'next/link'
import { Currencies, PlanKey, Intervals } from './types'
import { useRouter } from 'next/router'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Stripe } from '@stripe/stripe-js'
import { sendGTMEvent } from '@next/third-parties/google'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js'
import { reportsCreatedPerMonth } from '@v0-report/core/src/utils/subscription-limit'
import { ReferralContext } from '@/providers/referral'
import { useContext } from 'react'

const planDetails = {
  personal: {
    description:
      'Ideal for individuals and freelancers needing to generate professional documents.',
    features: [
      `${reportsCreatedPerMonth['PERSONAL']} reports/month`,
      'Automated web scraping',
      'Google Docs integration',
      'Standard security',
    ],
  },
  consultant: {
    description:
      'Designed for consultants looking to create detailed and branded documents for clients.',
    features: [
      'Everything in Personal',
      `${reportsCreatedPerMonth['CONSULTANT']} reports/month`,
      'White-label reports',
      'Email support',
      'Advanced analytics',
      'Document sharing',
    ],
  },
  business: {
    description:
      'Perfect for large organizations requiring extensive collaboration and report generation capabilities.',
    features: [
      'Everything in Consultant',
      `${reportsCreatedPerMonth['BUSINESS']} reports/month`,
      'Dedicated account manager',
      'Priority email support',
      '24/7 support',
      'Enterprise-grade security',
    ],
  },
}

const currencyToSymbol: Record<Currencies, string> = {
  usd: '$',
  eur: '€',
  gbp: '£',
}

export const PricingBox = ({
  planKey,
  plans,
  currency,
  interval,
  stripe,
}: {
  planKey: PlanKey
  plans: Awaited<ReturnType<typeof getStripePrices>>
  currency: Currencies
  interval: Intervals
  stripe: Promise<Stripe | null>
}) => {
  const referral = useContext(ReferralContext)

  const router = useRouter()

  const { mutateAsync: getBillingPortalUrl, isLoading: getBillingPortalLoading } =
    trpc.getBillingPortalUrl.useMutation(undefined)

  const { user } = useUser()

  const { mutateAsync, data, reset } = trpc.getStripeSessionIds.useMutation()

  const { mutateAsync: processSubscription, isLoading: processSubscriptionLoading } = useMutation({
    mutationFn: async (planKey: PlanKey) => {
      if (!planKey) {
        toast.error('Sorry something went wrong, please try a different plan')
        return
      }

      const plan = plans[planKey]

      const { sessionId, clientSecret } = await mutateAsync({
        planId: plan.id,
        currency,
        referral,
      })

      if (!sessionId || !clientSecret) {
        toast.error('Sorry something went wrong')
        return
      }

      sendGTMEvent({
        event: 'begin_checkout',
        currency: currency,
        value: plan.prices[currency] / 100,
      })
    },
  })

  const buttonToShow = (planKey: PlanKey) => {
    if (!user) {
      return (
        <Link href="/auth/login" passHref>
          <Button className="inline-flex items-center justify-center rounded-md w-full bg-gray-900 text-gray-50  px-4 py-2 text-sm font-medium shadow transition ease-in-out duration-150 hover:focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:pointer-events-none disabled:opacity-50 hover:bg-gray-800">
            Create account
          </Button>
        </Link>
      )
    }

    if (!user.publicMetadata.isSubscribed) {
      return (
        <Button
          onClick={() => processSubscription(planKey)}
          className="inline-flex items-center justify-center rounded-md w-full bg-gray-900 text-gray-50  px-4 py-2 text-sm font-medium shadow transition ease-in-out duration-150 hover:focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:pointer-events-none disabled:opacity-50 hover:bg-gray-800"
          disabled={processSubscriptionLoading}
        >
          {/* {processSubscriptionLoading ? <TinyLoader /> : 'Start Trial'} */}
          {processSubscriptionLoading ? <TinyLoader /> : 'Get started'}
        </Button>
      )
    }

    return (
      <Button
        onClick={async () => {
          const url = await getBillingPortalUrl()

          if (url) {
            router.push(url.billingPortalUrl)
          } else {
            toast.error(
              'Sorry, something went wrong getting your billing portal. Please try again later.'
            )
          }
        }}
        className="inline-flex items-center justify-center rounded-md w-full bg-gray-900 text-gray-50  px-4 py-2 text-sm font-medium shadow transition ease-in-out duration-150 hover:focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:pointer-events-none disabled:opacity-50 hover:bg-gray-800"
        disabled={processSubscriptionLoading}
      >
        {getBillingPortalLoading ? <TinyLoader /> : <span>Manage Subscription</span>}
      </Button>
    )
  }

  const plan = plans[planKey]
  const isConsultantPlan = planKey.includes('Consultant')

  let planType

  if (planKey.includes('Business')) {
    planType = 'business'
  } else if (planKey.includes('Consultant')) {
    planType = 'consultant'
  } else {
    planType = 'personal'
  }

  const details = planDetails[planType as keyof typeof planDetails]

  return (
    <div
      key={planKey}
      className="grid w-full h-full object-cover transition-transform duration-500 ease-in-out transform hover:scale-105"
    >
      <div
        className={`${
          isConsultantPlan
            ? ' shadow-lg hover:shadow-lg   border-sky-200 hover:border-sky-300 shadow-sky-400/60 hover:shadow-sky-500/60 '
            : 'shadow-md hover:shadow-lg  border-gray-200 hover:border-gray-300   '
        }  bg-white border rounded-lg w-full h-fit align-middle flex flex-col `}
      >
        {isConsultantPlan && (
          <div className="h-full rounded-t-lg text-sky-600 bg-gray-100 flex justify-center items-center py-2">
            <span className="font-semibold ">Most Popular</span>
          </div>
        )}

        <div className="p-6">
          <div className="mt-4 mb-6">
            <h2 className="text-2xl font-bold mb-4 text-gray-900 ">
              {planKey.replace(interval, '')}
            </h2>
            <p className="font-medium text-gray-500 mb-4 ">{details.description}</p>
            <div className="bg-zinc-100 w-full mt-6 py-1 rounded">
              <p className="text-center">
                <span className="font-semibold">7-Day</span> Free Trial
              </p>
            </div>
          </div>
          {planKey.replace(interval, '') !== 'business' && (
            <div className="flex flex-col items-start mb-6">
              <div className="flex items-center">
                <h3 className="text-4xl font-bold flex-none">
                  {currencyToSymbol[currency]}
                  {interval === 'yearly'
                    ? Math.round(plan.prices[currency] / 100 / 12)
                    : Math.round(plan.prices[currency] / 100)}
                </h3>
                <p className="font-medium text-sm text-gray-500 ml-2">
                  / {interval === 'yearly' ? 'month' : plan.interval}
                  {interval === 'yearly' && <span> billed annually</span>}
                </p>
              </div>
              {interval === 'yearly' && (
                <p className="text-sm text-gray-500 mt-2">
                  Save {currencyToSymbol[currency]}
                  {Math.round(
                    plans[planKey.replace('yearly', 'monthly') as keyof typeof plans].prices[
                      currency
                    ] /
                      100 -
                      plan.prices[currency] / 12 / 100
                  )}{' '}
                  per month
                </p>
              )}
            </div>
          )}

          <div className="mb-6">{buttonToShow(planKey)}</div>

          <ul className="ml-3 mb-6 space-y-2">
            {details.features.map((feature, index) => {
              if (feature === 'separation') {
                return (
                  <div key={index} className="w-full  flex items-center">
                    <Separator className="w-1/3" />
                    <div className="w-1/3 text-center">+</div>
                    <Separator className="w-1/3" />
                  </div>
                )
              }

              return (
                <li key={index} className="text-gray-700">
                  <CheckCircle className="inline-block h-4 w-4 mr-2 text-sky-500" />
                  {feature}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
      <Dialog
        open={Boolean(data?.clientSecret)}
        onOpenChange={(open) => {
          if (open === false) {
            reset()
          }
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-scroll ">
          <EmbeddedCheckoutProvider
            stripe={stripe}
            options={{
              clientSecret: data?.clientSecret as string,
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </DialogContent>
      </Dialog>
    </div>
  )
}
